import { useState, useEffect, useRef } from "react";

const WHATSAPP_NUMBER = "923135306503"; // international format, no + or spaces

const INFO = {
  name: "Muhammad Yasir Abbas",
  shortName: "Yasir",
  title: "Full-Stack Web Developer",
  email: "368yasir@gmail.com",
  location: "Pakistan",
  bio: "I build scalable, secure, and reliable web applications — from Angular-to-React migrations to fintech dashboards and logistics platforms. Focused on clean architecture, complex problem-solving, and shipping software that holds up under real use.",
  socials: {
    github: "https://github.com/Yasir950",
    linkedin: "https://www.linkedin.com/in/muhammadyasirabbas/",
    upwork: "https://www.upwork.com/freelancers/~017e36d0561306b1d1",
    instagram: "https://www.instagram.com/m_yasirabs/",
    whatsapp: `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Yasir, I found your portfolio and I'd like to talk about a project.")}`,
  },
  skills: [
    { name: "React.js", group: "Frontend" },
    { name: "Angular", group: "Frontend" },
    { name: "JavaScript / TypeScript", group: "Frontend" },
    { name: "Node.js", group: "Backend" },
    { name: "Express.js", group: "Backend" },
    { name: "Python", group: "Backend" },
    { name: "MongoDB", group: "Data" },
    { name: "SQL", group: "Data" },
    { name: "REST APIs", group: "Backend" },
    { name: "Firebase", group: "Infra" },
    { name: "Git", group: "Infra" },
  ],
  experience: [
    { company: "True Meridian", role: "Software Engineer", start: new Date(2024, 0, 3), end: null, color: "#07934d" },
    { company: "Infini8ai", role: "Software Engineer", start: new Date(2022, 0, 7), end: new Date(2024, 0, 3), color: "#B5791E" },
    { company: "W4Work", role: "Software Engineer", start: new Date(2021, 0, 6), end: new Date(2022, 0, 6), color: "#3E6478" },
  ],
  education: [
    { institution: "International Islamic University, Islamabad", degree: "BS Software Engineering", period: "June 2017 – June 2021" },
  ],
  projects: [
    {
      ref: "DOC-01",
      title: "Fintrix",
      subtitle: "FBR Digital Invoicing Portal",
      description: "A digital tax e-invoicing platform built for FBR (Federal Board of Revenue) compliance — handling client management, invoice generation and submission, bulk data uploads, and real-time reporting for businesses filing digital tax invoices.",
      link: "https://dis.fintrix.pk/",
      tag: "GovTech / Fintech",
      stack: ["Angular", "Python", "MongoDB"],
    },
    {
      ref: "DOC-02",
      title: "Khat Al Jazeera Marina Cargo",
      subtitle: "Kajship",
      description: "A vehicle shipping and fleet logistics platform. Ship vehicles anytime, anywhere; track every vehicle at every stage of the journey; manage a whole fleet and balances from one dashboard, with real-time status and photo updates.",
      link: "https://app.kajship.com/",
      tag: "Logistics / SaaS",
      stack: ["React", "Node.js", "SQL"],
    },
    {
      ref: "DOC-03",
      title: "OmegaJam",
      subtitle: "B2B2C Sports Gaming Platform",
      description: "A revolutionary B2B2C sports-based gaming platform that lets charities, retailers, celebrities, and influencers engage and monetize audiences through interactive sports games and challenges. Built the front end in Angular for scale and performance, integrated with secure Node.js/Express REST APIs.",
      link: "https://omegajam.com",
      tag: "Gaming / B2B2C",
      stack: ["AngularJS", "Express.js", "Node.js", "MongoDB"],
    },
    {
      ref: "DOC-04",
      title: "TradersHub",
      subtitle: "Crypto Trading & Rental Platform",
      description: "A platform to trade hundreds of cryptocurrencies and derivatives, paired with a fully operational long-term rental marketplace, built to leverage blockchain technology for transparency and settlement.",
      link: "https://tradershub.us/",
      tag: "Crypto / Fintech",
      stack: ["Node.js", "React", "SQL"],
    },
    {
      ref: "DOC-05",
      title: "Seven Stars",
      subtitle: "Real Estate Platform",
      description: "Real estate platform specializing in buying, selling, and renting properties, with listing management and search built for a smooth buyer/seller experience.",
      link: "https://sevenstarsre.com/",
      tag: "Real Estate",
      stack: ["React", "Node.js"],
    },
    {
      ref: "DOC-06",
      title: "ResearchPal",
      subtitle: "AI Research Assistant",
      description: "AI-powered research platform for scholars and academics, built to streamline knowledge discovery, citation handling, and literature review workflows.",
      link: "https://researchpal.co/",
      tag: "AI / SaaS",
      stack: ["React", "Node.js"],
    },
  ],
};

const NAV = ["About", "Skills", "Projects", "Experience", "Contact"];

// Dynamic tenure calculation — anchored to June 2021, recalculated on every render
function getTenure(fromYear = 2021, fromMonth = 5) {
  const start = new Date(fromYear, fromMonth, 1);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years, months };
}

function formatDuration(start, end) {
  const endDate = end || new Date();
  let years = endDate.getFullYear() - start.getFullYear();
  let months = endDate.getMonth() - start.getMonth();
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0 || years === 0) parts.push(`${months}m`);
  return parts.join(" ");
}

function formatPeriod(start, end) {
  const opts = { month: "short", year: "numeric" };
  const s = start.toLocaleDateString("en-US", opts);
  const e = end ? end.toLocaleDateString("en-US", opts) : "Present";
  return `${s} — ${e}`;
}

export default function Portfolio() {
  const [active, setActive] = useState("About");
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState({});
  const sectionRefs = useRef({});
  const tenure = getTenure();
  const totalYearsLabel = tenure.years < 1 ? "<1" : `${tenure.years}+`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible((v) => ({ ...v, [e.target.id]: true }));
            setActive(e.target.dataset.section || active);
          }
        });
      },
      { threshold: 0.2 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const fadeIn = (id) => ({
    opacity: visible[id] ? 1 : 0,
    transform: visible[id] ? "translateY(0)" : "translateY(28px)",
    transition: "opacity 0.6s ease, transform 0.6s ease",
  });

  return (
    <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", background: "#FFFFFF", color: "#14171C", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=JetBrains+Mono:wght@400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #FFFFFF; }
        ::-webkit-scrollbar-thumb { background: #B5791E; border-radius: 4px; }
        a { text-decoration: none; color: inherit; }
        .mono { font-family: 'JetBrains Mono', monospace; }
        .display { font-family: 'Space Grotesk', sans-serif; }
        .tag { display: inline-block; padding: 3px 10px; border-radius: 3px; font-size: 10.5px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; font-family: 'JetBrains Mono', monospace; }
        .nav-link { cursor: pointer; font-size: 12.5px; font-weight: 500; letter-spacing: .08em; text-transform: uppercase; padding: 6px 0; color: #6E7278; transition: color .2s; font-family: 'JetBrains Mono', monospace; }
        .nav-link:hover, .nav-link.active { color: #B5791E; }
        .btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 26px; border-radius: 3px; font-weight: 600; font-size: 13.5px; cursor: pointer; border: none; transition: all .2s; font-family: 'Space Grotesk', sans-serif; letter-spacing: .01em; }
        .btn-primary { background: #B5791E; color: #FFFFFF; }
        .btn-primary:hover { background: #9c6819; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(181,121,30,.25); }
        .btn-whatsapp { background: #E7F5EC; color: #1F9D55; border: 1.5px solid #BFE6CB; }
        .btn-whatsapp:hover { border-color: #1F9D55; transform: translateY(-1px); }
        .btn-ghost { background: transparent; color: #14171C; border: 1.5px solid #E1E3E7; }
        .btn-ghost:hover { border-color: #B5791E; color: #B5791E; transform: translateY(-1px); }
        .card { background: #FFFFFF; border: 1px solid #E1E3E7; border-radius: 6px; transition: border-color .25s, transform .25s, box-shadow .25s; position: relative; overflow: hidden; }
        .card:hover { border-color: #B5791E; transform: translateY(-4px); box-shadow: 0 16px 40px rgba(20,23,28,.08); }
        .skill-chip { padding: 7px 14px; border-radius: 3px; background: #FFFFFF; border: 1px solid #E1E3E7; font-size: 12.5px; font-weight: 500; color: #3A3D44; transition: all .2s; cursor: default; font-family: 'JetBrains Mono', monospace; }
        .skill-chip:hover { border-color: #B5791E; color: #B5791E; background: rgba(181,121,30,.06); }
        .social-icon { width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; border-radius: 4px; border: 1px solid #E1E3E7; color: #6E7278; transition: all .2s; cursor: pointer; font-size: 16px; }
        .social-icon:hover { border-color: #B5791E; color: #B5791E; background: rgba(181,121,30,.06); transform: translateY(-2px); }
        .input { width: 100%; padding: 12px 16px; background: #FFFFFF; border: 1px solid #E1E3E7; border-radius: 4px; color: #14171C; font-size: 14px; font-family: 'Space Grotesk', sans-serif; outline: none; transition: border-color .2s; }
        .input:focus { border-color: #B5791E; }
        .input::placeholder { color: #A8ABB2; }
        .section-label { font-size: 11px; font-weight: 600; letter-spacing: .16em; text-transform: uppercase; color: #B5791E; margin-bottom: 12px; font-family: 'JetBrains Mono', monospace; }
        .section-title { font-family: 'Space Grotesk', sans-serif; font-size: clamp(26px, 4vw, 40px); font-weight: 700; color: #14171C; line-height: 1.1; letter-spacing: -.01em; }
        .divider { width: 48px; height: 3px; background: #B5791E; border-radius: 2px; margin: 20px 0 36px; }
        .exp-dot { width: 11px; height: 11px; border-radius: 2px; flex-shrink: 0; margin-top: 4px; transform: rotate(45deg); }
        .exp-line { width: 2px; background: #E1E3E7; flex: 1; min-height: 40px; }
        .stamp { font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .12em; color: #1F9D55; border: 1px solid rgba(31,157,85,.4); padding: 3px 8px; border-radius: 3px; transform: rotate(-4deg); display: inline-flex; align-items: center; gap: 5px; }
        @media (max-width: 700px) {
          .hero-grid { flex-direction: column !important; }
          .projects-grid { grid-template-columns: 1fr !important; }
          .nav-container { gap: 16px !important; }
          .meta-bar { flex-wrap: wrap; gap: 10px !important; }
        }
      `}</style>

      {/* META BAR — document-style header, ties into the invoicing/ledger world he builds in */}
      <div className="mono meta-bar" style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px clamp(20px,5vw,80px)", fontSize: 11, color: "#8A8E96",
        borderBottom: "1px solid #E4E6EA", letterSpacing: ".04em",
      }}>
        <span>REF// PORTFOLIO-{new Date().getFullYear()}</span>
        <span style={{ color: "#1F9D55" }}>● STATUS// AVAILABLE FOR WORK</span>
        <span>SINCE// JUN 2021 · {tenure.years}Y {tenure.months}M</span>
      </div>

      {/* NAV */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: scrolled ? "rgba(255,255,255,.94)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: scrolled ? "1px solid #E4E6EA" : "1px solid transparent",
        transition: "all .3s",
        padding: "0 clamp(20px,5vw,80px)",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <span className="display" style={{ fontWeight: 700, fontSize: 20, color: "#14171C", letterSpacing: "-.01em" }}>
            YA<span style={{ color: "#B5791E" }}>.</span>
          </span>
          <div className="nav-container" style={{ display: "flex", gap: 32 }}>
            {NAV.map((n) => (
              <span key={n} className={`nav-link ${active === n ? "active" : ""}`} onClick={() => scrollTo(n.toLowerCase())}>
                {n}
              </span>
            ))}
          </div>
          <a href={INFO.socials.whatsapp} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ padding: "8px 18px", fontSize: 13 }}>
            Hire Me
          </a>
        </div>
      </nav>

      {/* HERO */}
      <section
        id="about"
        data-section="About"
        ref={(el) => { sectionRefs.current.about = el; }}
        style={{ minHeight: "92vh", display: "flex", alignItems: "center", padding: "60px clamp(20px,5vw,80px) 60px" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto", width: "100%" }}>
          <div className="hero-grid" style={{ display: "flex", gap: 60, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <div id="hero" ref={(el) => { sectionRefs.current.hero = el; }} style={fadeIn("hero")}>
                <div className="section-label">{INFO.title}</div>
                <h1 className="display" style={{ fontSize: "clamp(28px,6vw,50px)", fontWeight: 700, color: "#14171C", lineHeight: 1.08, marginBottom: 24, letterSpacing: "-.02em" }}>
                  {INFO.name}
                </h1>
                <p style={{ fontSize: 17, color: "#4B4F58", lineHeight: 1.8, maxWidth: 540, marginBottom: 36 }}>
                  {INFO.bio}
                </p>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 40 }}>
                  <button className="btn btn-primary" onClick={() => scrollTo("projects")}>View Projects</button>
                  <a href={INFO.socials.whatsapp} target="_blank" rel="noreferrer" className="btn btn-whatsapp">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.85 14.05c-.25.7-1.45 1.34-2 1.42-.51.08-1.15.11-1.86-.12-.43-.14-.98-.32-1.68-.63-2.96-1.28-4.89-4.25-5.04-4.45-.15-.2-1.2-1.6-1.2-3.05s.75-2.17 1.02-2.47c.26-.29.57-.36.76-.36h.55c.18 0 .42-.03.64.49.25.6.85 2.05.92 2.2.08.15.13.32.02.52-.1.2-.16.32-.31.5-.16.18-.33.4-.47.53-.16.15-.32.32-.14.63.19.32.83 1.37 1.78 2.22 1.22 1.09 2.25 1.43 2.57 1.6.32.16.51.13.7-.08.2-.2.83-.97 1.05-1.3.22-.32.44-.27.74-.16.3.1 1.9.9 2.23 1.06.32.16.54.24.62.38.08.14.08.79-.18 1.49z"/></svg>
                    Chat on WhatsApp
                  </a>
                  <a href={`mailto:${INFO.email}`} className="btn btn-ghost">Get In Touch</a>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  {[
                    { href: INFO.socials.github, svg: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg> },
                    { href: INFO.socials.linkedin, svg: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                    { href: INFO.socials.upwork, svg: <svg viewBox="0 0 24 24" fill="currentColor" width="17" height="17"><path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.546-1.405 0-2.543-1.14-2.543-2.546V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3-2.439-5.439-5.439-5.439z"/></svg> },
                    { href: `mailto:${INFO.email}`, svg: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="17" height="17"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg> },
                  ].map((s, i) => (
                    <a key={i} href={s.href} target="_blank" rel="noreferrer" className="social-icon">{s.svg}</a>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <div style={{
                width: 240, height: 300, borderRadius: 6,
                background: "#FFFFFF", border: "1px solid #E1E3E7",
                boxShadow: "0 4px 24px rgba(20,23,28,.05)",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16,
                position: "relative",
              }}>
                <div style={{ position: "absolute", top: 14, left: 14, right: 14, display: "flex", justifyContent: "space-between" }} className="mono">
                  <span style={{ fontSize: 10, color: "#8A8E96" }}>ID-0721</span>
                  <span style={{ fontSize: 10, color: "#1F9D55" }}>ACTIVE</span>
                </div>
                <div style={{
                  width: 88, height: 88, borderRadius: "50%",
                  background: "linear-gradient(135deg, #B5791E 0%, #3E6478 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 34, fontWeight: 700, color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif",
                }}>
                  MY
                </div>
                <div style={{ textAlign: "center" }}>
                  <div className="display" style={{ fontSize: 15, fontWeight: 700, color: "#14171C" }}>{INFO.shortName}</div>
                  <div className="mono" style={{ fontSize: 10, color: "#6E7278", marginTop: 4 }}>{INFO.location}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginTop: 64, maxWidth: 520 }}>
            {[[totalYearsLabel, "Years Experience"], [`${INFO.projects.length}+`, "Projects Shipped"], ["3", "Companies Served"]].map(([n, l]) => (
              <div key={l} style={{ textAlign: "center", padding: "20px 12px", background: "#F8F8F6", borderRadius: 6, border: "1px solid #E1E3E7" }}>
                <div className="display" style={{ fontSize: 30, fontWeight: 700, color: "#B5791E" }}>{n}</div>
                <div className="mono" style={{ fontSize: 11, color: "#6E7278", marginTop: 6, letterSpacing: ".03em" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section
        id="skills"
        data-section="Skills"
        ref={(el) => { sectionRefs.current.skills = el; }}
        style={{ padding: "80px clamp(20px,5vw,80px)" }}
      >
        <div id="skills-inner" ref={(el) => { sectionRefs.current["skills-inner"] = el; }} style={{ maxWidth: 1100, margin: "0 auto", ...fadeIn("skills-inner") }}>
          <div className="section-label">What I work with</div>
          <h2 className="section-title">Technical Skills</h2>
          <div className="divider" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {INFO.skills.map((s) => (
              <span key={s.name} className="skill-chip">{s.name}</span>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section
        id="projects"
        data-section="Projects"
        ref={(el) => { sectionRefs.current.projects = el; }}
        style={{ padding: "80px clamp(20px,5vw,80px)", background: "#F4F5F7" }}
      >
        <div id="projects-inner" ref={(el) => { sectionRefs.current["projects-inner"] = el; }} style={{ maxWidth: 1100, margin: "0 auto", ...fadeIn("projects-inner") }}>
          <div className="section-label">What I've built</div>
          <h2 className="section-title">Featured Projects</h2>
          <div className="divider" />
          <div className="projects-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 }}>
            {INFO.projects.map((p, i) => (
              <a key={i} href={p.link} target="_blank" rel="noreferrer" className="card" style={{ padding: 26, display: "block" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                  <span className="mono" style={{ fontSize: 11, color: "#8A8E96", letterSpacing: ".08em" }}>{p.ref}</span>
                  <span className="stamp">● LIVE</span>
                </div>
                <span className="tag" style={{ background: "rgba(181,121,30,.08)", color: "#B5791E", border: "1px solid rgba(181,121,30,.25)" }}>{p.tag}</span>
                <h3 className="display" style={{ fontSize: 19, fontWeight: 700, color: "#14171C", marginTop: 14, marginBottom: 2 }}>{p.title}</h3>
                <div className="mono" style={{ fontSize: 11.5, color: "#3E6478", marginBottom: 12 }}>{p.subtitle}</div>
                <p style={{ fontSize: 13.5, color: "#4B4F58", lineHeight: 1.75, marginBottom: 18 }}>{p.description}</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
                  {p.stack.map((s) => (
                    <span key={s} className="mono" style={{ fontSize: 10.5, color: "#6E7278", border: "1px solid #E1E3E7", padding: "3px 8px", borderRadius: 3 }}>{s}</span>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#B5791E", fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif" }}>
                  <span>View Project</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE */}
      <section
        id="experience"
        data-section="Experience"
        ref={(el) => { sectionRefs.current.experience = el; }}
        style={{ padding: "80px clamp(20px,5vw,80px)" }}
      >
        <div id="exp-inner" ref={(el) => { sectionRefs.current["exp-inner"] = el; }} style={{ maxWidth: 1100, margin: "0 auto", ...fadeIn("exp-inner") }}>
          <div className="section-label">Career path — updates automatically</div>
          <h2 className="section-title">Experience &amp; Education</h2>
          <div className="divider" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60 }}>
            <div>
              <h3 className="mono" style={{ fontSize: 12, fontWeight: 600, color: "#6E7278", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 28 }}>Work</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {INFO.experience.map((e, i) => (
                  <div key={i} style={{ display: "flex", gap: 16 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div className="exp-dot" style={{ background: e.color }} />
                      {i < INFO.experience.length - 1 && <div className="exp-line" />}
                    </div>
                    <div style={{ paddingBottom: i < INFO.experience.length - 1 ? 32 : 0 }}>
                      <div className="display" style={{ fontSize: 17, fontWeight: 700, color: "#14171C" }}>{e.company}</div>
                      <div style={{ fontSize: 14, color: "#B5791E", fontWeight: 500, marginTop: 2, fontFamily: "'Space Grotesk', sans-serif" }}>{e.role}</div>
                      <div className="mono" style={{ fontSize: 11.5, color: "#6E7278", marginTop: 6 }}>
                        {formatPeriod(e.start, e.end)} · {formatDuration(e.start, e.end)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="mono" style={{ fontSize: 12, fontWeight: 600, color: "#6E7278", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 28 }}>Education</h3>
              {INFO.education.map((e, i) => (
                <div key={i} style={{ display: "flex", gap: 16 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div className="exp-dot" style={{ background: "#3E6478" }} />
                  </div>
                  <div>
                    <div className="display" style={{ fontSize: 17, fontWeight: 700, color: "#14171C" }}>{e.degree}</div>
                    <div style={{ fontSize: 14, color: "#3E6478", fontWeight: 500, marginTop: 2, fontFamily: "'Space Grotesk', sans-serif" }}>{e.institution}</div>
                    <div className="mono" style={{ fontSize: 11.5, color: "#6E7278", marginTop: 6 }}>{e.period}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        data-section="Contact"
        ref={(el) => { sectionRefs.current.contact = el; }}
        style={{ padding: "80px clamp(20px,5vw,80px) 100px", background: "#F4F5F7" }}
      >
        <div id="contact-inner" ref={(el) => { sectionRefs.current["contact-inner"] = el; }} style={{ maxWidth: 640, margin: "0 auto", textAlign: "center", ...fadeIn("contact-inner") }}>
          <div className="section-label">Let's work together</div>
          <h2 className="section-title" style={{ marginBottom: 16 }}>Get In Touch</h2>
          <p style={{ color: "#4B4F58", fontSize: 16, lineHeight: 1.8, marginBottom: 36 }}>
            Whether you have a project in mind or just want to say hello — reach out over WhatsApp for the fastest reply, or drop a message below.
          </p>
          <a href={INFO.socials.whatsapp} target="_blank" rel="noreferrer" className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center", padding: "14px 24px", fontSize: 15, marginBottom: 32 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.85 14.05c-.25.7-1.45 1.34-2 1.42-.51.08-1.15.11-1.86-.12-.43-.14-.98-.32-1.68-.63-2.96-1.28-4.89-4.25-5.04-4.45-.15-.2-1.2-1.6-1.2-3.05s.75-2.17 1.02-2.47c.26-.29.57-.36.76-.36h.55c.18 0 .42-.03.64.49.25.6.85 2.05.92 2.2.08.15.13.32.02.52-.1.2-.16.32-.31.5-.16.18-.33.4-.47.53-.16.15-.32.32-.14.63.19.32.83 1.37 1.78 2.22 1.22 1.09 2.25 1.43 2.57 1.6.32.16.51.13.7-.08.2-.2.83-.97 1.05-1.3.22-.32.44-.27.74-.16.3.1 1.9.9 2.23 1.06.32.16.54.24.62.38.08.14.08.79-.18 1.49z"/></svg>
            Chat on WhatsApp
          </a>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
            <input className="input" placeholder="Your name" />
            <input className="input" placeholder="Your email" />
            <textarea className="input" placeholder="Your message" rows={4} style={{ resize: "vertical" }} />
          </div>
          <a href={`mailto:${INFO.email}`} className="btn btn-ghost" style={{ width: "100%", justifyContent: "center", padding: "14px 24px", fontSize: 15 }}>
            Send Email Instead
          </a>
          <div style={{ marginTop: 36, padding: "20px", background: "#FFFFFF", borderRadius: 6, border: "1px solid #E1E3E7" }} className="mono">
            <div style={{ fontSize: 12, color: "#6E7278", marginBottom: 8 }}>Or reach me directly at</div>
            <a href={`mailto:${INFO.email}`} style={{ color: "#B5791E", fontWeight: 600, fontSize: 14.5 }}>{INFO.email}</a>
          </div>
        </div>
      </section>

      {/* WHATSAPP FLOATING BUTTON */}
      <a
        href={INFO.socials.whatsapp}
        target="_blank"
        rel="noreferrer"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 200,
          width: 56, height: 56, borderRadius: "50%",
          background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 8px 24px rgba(37,211,102,.35)",
        }}
        aria-label="Chat on WhatsApp"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#FFFFFF"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm5.85 14.05c-.25.7-1.45 1.34-2 1.42-.51.08-1.15.11-1.86-.12-.43-.14-.98-.32-1.68-.63-2.96-1.28-4.89-4.25-5.04-4.45-.15-.2-1.2-1.6-1.2-3.05s.75-2.17 1.02-2.47c.26-.29.57-.36.76-.36h.55c.18 0 .42-.03.64.49.25.6.85 2.05.92 2.2.08.15.13.32.02.52-.1.2-.16.32-.31.5-.16.18-.33.4-.47.53-.16.15-.32.32-.14.63.19.32.83 1.37 1.78 2.22 1.22 1.09 2.25 1.43 2.57 1.6.32.16.51.13.7-.08.2-.2.83-.97 1.05-1.3.22-.32.44-.27.74-.16.3.1 1.9.9 2.23 1.06.32.16.54.24.62.38.08.14.08.79-.18 1.49z"/></svg>
      </a>

      {/* FOOTER */}
      <footer style={{ padding: "24px clamp(20px,5vw,80px)", borderTop: "1px solid #E4E6EA", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <span className="display" style={{ fontWeight: 700, fontSize: 18, color: "#14171C" }}>YA<span style={{ color: "#B5791E" }}>.</span></span>
        <span className="mono" style={{ fontSize: 11.5, color: "#8A8E96" }}>© {new Date().getFullYear()} Muhammad Yasir Abbas · All Rights Reserved</span>
        <div style={{ display: "flex", gap: 20 }}>
          {NAV.map((n) => (
            <span key={n} className="nav-link" onClick={() => scrollTo(n.toLowerCase())} style={{ fontSize: 11.5 }}>{n}</span>
          ))}
        </div>
      </footer>
    </div>
  );
}